import {decodeHTML} from "../../../../app";
import {AdditionalExtractionMethod, ExtractionMethod} from "../../models/ttyg/agents";
import 'angular/core/services/similarity.service';
import 'angular/core/services/connectors.service';
import 'angular/core/services/ttyg.service';
import 'angular/core/directives/multiselect-search/multiselect-search.directive';
import 'angular/rest/repositories.rest.service';
import 'angular/ttyg/controllers/agent-instructions-explain-modal.controller';
import 'angular/ttyg/services/externalIntegrationModal.service';
import {REPOSITORY_PARAMS} from "../../models/repository/repository";
import {TTYGEventName} from "../services/ttyg-context.service";
import {AGENT_OPERATION, TTYG_ERROR_MSG_LENGTH} from "../services/constants";
import {mapUriAsNtripleAutocompleteResponse} from "../../rest/mappers/autocomplete-mapper";
import {DocumentationUrlResolver} from "../../utils/documentation-url-resolver";
import {LoggerProvider} from "../../core/services/logger-provider";
import {SelectMenuOptionsModel} from "../../models/form-fields";
import {SimilarityInstanceType} from '../../models/similarity/similarity-instance-type';

const logger = LoggerProvider.logger;
/**
 * @typedef {Object} SimilarityIndexOption
 * @property {string} provider
 * @property {string} label
 * @property {string} value
 * @property {string} group
 */


angular
    .module('graphdb.framework.ttyg.controllers.agent-settings-modal', [
        'graphdb.framework.core.services.similarity',
        'graphdb.framework.core.services.connectors',
        'graphdb.framework.rest.repositories.service',
        'graphdb.framework.ttyg.controllers.agent-instructions-explain-modal',
        'graphdb.framework.ttyg.services.externalIntegrationModal',
        'ngTagsInput',
        'graphdb.framework.core.directives.multiselect',
    ])
    .constant('ExtractionMethodTemplates', {
        'iri_discovery_search': 'iri-discovery-search',
        'autocomplete_iri_discovery_search': 'autocomplete-iri-discovery-search',
    })
    .filter('map', function() {
        return function(arr, key) {
            if (!Array.isArray(arr)) {
                return arr;
            }
            return arr.map(function(o) {
                return o[key];
            });
        };
    })
    .controller('AgentSettingsModalController', AgentSettingsModalController);

AgentSettingsModalController.$inject = [
    '$scope',
    '$uibModalInstance',
    'ModalService',
    '$uibModal',
    'SimilarityService',
    'ConnectorsService',
    'RepositoriesRestService',
    '$sce',
    'toastr',
    'UriUtils',
    '$translate',
    'dialogModel',
    'TTYGContextService',
    'TTYGService',
    'ExtractionMethodTemplates',
    'AutocompleteService',
    'AutocompleteRestService',
    'ExternalIntegrationModalService',
    'productInfo'];

function AgentSettingsModalController(
    $scope,
    $uibModalInstance,
    ModalService,
    $uibModal,
    SimilarityService,
    ConnectorsService,
    RepositoriesRestService,
    $sce,
    toastr,
    UriUtils,
    $translate,
    dialogModel,
    TTYGContextService,
    TTYGService,
    ExtractionMethodTemplates,
    AutocompleteService,
    AutocompleteRestService,
    ExternalIntegrationModalService,
    productInfo) {
    // =========================
    // Private variables
    // =========================

    const CHAT_GPT_RETRIEVAL_CONNECTOR_NAME = 'ChatGPT Retrieval';

    // =========================
    // Public variables
    // =========================

    $scope.AGENT_OPERATION = AGENT_OPERATION;

    /**
     * The operation type for the modal. This can be one of <code>AGENT_OPERATION</code> constants.
     */
    $scope.operation = dialogModel.operation;

    /**
     * Flag to control the visibility of the loader when creating an agent.
     * @type {boolean}
     */
    $scope.savingAgent = false;

    /**
     * The model used in the form.
     * @type {AgentFormModel|*}
     */
    $scope.agentFormModel = dialogModel.agentFormModel;

    /**
     * The active repository info model.
     * @type {RepositoryInfoModel|*}
     */
    $scope.activeRepositoryInfo = dialogModel.activeRepositoryInfo;

    /**
     * Flags used to show/hide the loader for the extraction methods when the user opens a panel and some data needs to
     * be loaded.
     * @type {{[string]: boolean}} - the key is the extraction method types in <code>ExtractionMethod</code>
     */
    $scope.extractionMethodLoaderFlags = {
        [ExtractionMethod.SIMILARITY]: false,
    };

    /**
     * The repository select menu options model.
     * @type {SelectMenuOptionsModel[]}
     */
    $scope.activeRepositoryList = dialogModel.activeRepositoryList;

    /**
     * The extraction method types constants.
     * @type {{SPARQL: string, FTS_SEARCH: string, SIMILARITY: string, RETRIEVAL: string}}
     */
    $scope.extractionMethods = ExtractionMethod;

    /**
     * The additional extraction method types constants.
     * @type {{IRI_DISCOVERY_SEARCH: string, AUTOCOMPLETE_IRI_DISCOVERY_SEARCH: string}}
     */
    $scope.additionalExtractionMethods = AdditionalExtractionMethod;

    /**
     * The names of the template files, containing the Extraction Method Templates.
     * @type {Object<string, string>}
     */
    $scope.ExtractionMethodTemplates = ExtractionMethodTemplates;

    /**
     * Flag used to show/hide the advanced settings in the modal.
     * @type {boolean}
     */
    $scope.showAdvancedSettings = false;

    /**
     * Flag used to show/hide the high temperature warning in the modal.
     * @type {boolean}
     */
    $scope.showHighTemperatureWarning = false;

    /**
     * Flag used to control the visibility of the system instruction warning.
     * @type {boolean}
     */
    $scope.showSystemInstructionWarning = false;

    /**
     * The retrieval connectors to be used for the retrieval extraction method as select menu options.
     * @type {SelectMenuOptionsModel[]}
     */
    $scope.retrievalConnectors = [];

    /**
     * Flag used to show/hide the FTS enabled state in the modal.
     * @type {boolean}
     */
    $scope.ftsEnabled = false;

    /**
     * The autocomplete suggestions source for the 'Autocomplete IRI discovery' extraction method.
     * @type {*[]}
     */
    $scope.autocompleteSuggestions = [];

    /**
     * Flag to determine if the context size field should be visible in the form.
     * @type {boolean}
     */
    $scope.showContextSize = ($scope.agentFormModel.contextSize !== null);

    /**
     * The model for the vector fields select dropdown (Similarity search method).
     * @type {string[]}
     */
    $scope.vectorFields = [];

    $scope.documentationUrlForSimilarity = DocumentationUrlResolver.getDocumentationUrl(productInfo.productShortVersion, 'talk-to-graph.html#prerequisites-and-configuration');

    /**
     * The similarity index select options grouped by provider for the similarity search extraction method.
     * @type {*[]}
     */
    $scope.similarityOptionsGrouped = [];

    // =========================
    // Public functions
    // =========================

    /**
     * Sets the UI touched state and validation state for the extraction methods property so that the UI can show if
     * the user has selected at least one extraction method and warn him if he hasn't.
     *
     * @param {ExtractionMethodFormModel} extractionMethod
     */
    $scope.toggleExtractionMethod = (extractionMethod) => {
        extractionMethod.expanded = extractionMethod.selected;
        $scope.agentSettingsForm.extractionMethods.$setTouched();
        setExtractionMethodValidityStatus();
        extractionPanelToggleHandlers[extractionMethod.method](extractionMethod);
    };

    /**
     * Handles the panel toggle event for the extraction method. This is used to do some initialization when the user
     * opens the panel for a specific extraction method.
     * @param {ExtractionMethodFormModel} extractionMethod
     */
    $scope.onExtractionMethodPanelToggle = (extractionMethod) => {
        extractionMethod.toggleCollapse();
        extractionPanelToggleHandlers[extractionMethod.method](extractionMethod);
    };

    /**
     * Sets the UI touched state and validation state for the additional extraction methods property so that the UI can show if
     * the user has selected at least one extraction method.
     *
     * @param {AdditionalExtractionMethodFormModel} extractionMethod
     */
    $scope.toggleAdditionalExtractionMethod = (extractionMethod) => {
        extractionMethod.expanded = extractionMethod.selected;
        $scope.checkAutocompleteIndexEnabled();
        additionalExtractionPanelToggleHandlers[extractionMethod.method](extractionMethod);
    };

    /**
     * Handles the panel toggle event for the additional extraction method. This is used to do some initialization when the user
     * opens the panel for a specific extraction method.
     * @param {AdditionalExtractionMethodFormModel} extractionMethod
     */
    $scope.onAdditionalExtractionMethodPanelToggle = (extractionMethod) => {
        extractionMethod.toggleCollapse();
        additionalExtractionPanelToggleHandlers[extractionMethod.method](extractionMethod);
    };

    $scope.helpInfoForModel = {
        ttygHelpInfo: getModelHelpMessage(),
        linkText: $translate.instant('ttyg.agent.create_agent_modal.form.model.link_text'),
        documentationUrl: DocumentationUrlResolver.getDocumentationUrl(productInfo.productShortVersion, 'talk-to-graph.html#prerequisites-and-configuration'),
    };

    /**
     * Resolves the hint for the FTS search missing message. This is needed because the hint contains a html link that
     * should be properly rendered.
     * @return {*}
     */
    $scope.getFTSDisabledHelpMessage = () => {
        // The hint contains a html link which should be properly rendered.
        const message = decodeHTML(
            $translate.instant(
                'ttyg.agent.create_agent_modal.form.fts_search.fts_disabled_message',
                {repositoryEditPage: 'repository/edit/' + $scope.agentFormModel.repositoryId},
            ),
        );
        return $sce.trustAsHtml(message);
    };

    /**
     * Opens the 'Autocomplete index' view in a new tab.
     */
    $scope.goToAutocompleteView = (event) => {
        event.preventDefault();
        TTYGContextService.emit(TTYGEventName.GO_TO_AUTOCOMPLETE_INDEX_VIEW, {repositoryId: $scope.agentFormModel.repositoryId});
    };

    /**
     * Opens the 'Create Similarity' view in a new tab.
     */
    $scope.goToCreateSimilarityView = (event) => {
        event.preventDefault();
        TTYGContextService.emit(TTYGEventName.GO_TO_CREATE_SIMILARITY_VIEW, {repositoryId: $scope.agentFormModel.repositoryId});
    };

    /**
     * Opens the 'Connectors' view in a new tab.
     */
    $scope.goToConnectorsView = (event) => {
        event.preventDefault();
        TTYGContextService.emit(TTYGEventName.GO_TO_CONNECTORS_VIEW, {repositoryId: $scope.agentFormModel.repositoryId});
    };

    /**
     * Handles the selected agent operation which can be one of <code>AGENT_OPERATION</code> types. Respective handler
     * is called based on the operation type with the agent payload build based on the form model.
     * @return {Promise<void>}
     */
    $scope.ok = () => {
        const agentPayload = $scope.agentFormModel.toPayload();
        return agentOperationHandlers[$scope.operation](agentPayload);
    };

    /**
     * Closes the modal when the user cancels the agent creation.
     */
    $scope.cancel = () => {
        $uibModalInstance.dismiss({});
    };

    /**
     * Closes the modal when the user clicks the close button.
     */
    $scope.close = () => {
        $uibModalInstance.dismiss({});
    };

    /**
     * Updates the similarity search panel.
     *
     * @param {boolean} clearIndexSelection - If true, the selected index will be cleared.
     */
    $scope.updateSimilaritySearchPanel = (clearIndexSelection = false) => {
        const similaritySearchExtractionMethod = $scope.agentFormModel.assistantExtractionMethods.getSimilarityExtractionMethod();
        if (!similaritySearchExtractionMethod.selected) {
            return;
        }
        if (clearIndexSelection) {
            similaritySearchExtractionMethod.similarityIndex = null;
        }
        handleSimilaritySearchExtractionMethodPanelToggle(similaritySearchExtractionMethod, true);
    };

    /**
     * Updates the ChatGPT retrieval connector panel.
     *
     * @param {boolean} clearSelection - If true, the selected connector will be cleared.
     */
    $scope.updateRetrievalConnectorPanel = (clearSelection = false) => {
        const retrievalExtractionExtractionMethod = $scope.agentFormModel.assistantExtractionMethods.getRetrievalExtractionMethod();
        if (!retrievalExtractionExtractionMethod.selected) {
            return;
        }
        if (clearSelection) {
            retrievalExtractionExtractionMethod.retrievalConnectorInstance = null;
        }
        handleRetrievalConnectorExtractionMethodPanelToggle(retrievalExtractionExtractionMethod);
    };

    /**
     * Checks if the Full-Text Search (FTS) index is enabled for the selected repository.
     * Sets form validation state based on whether FTS is enabled and required.
     */
    $scope.checkIfFTSEnabled = () => {
        if (!$scope.agentFormModel.repositoryId) {
            $scope.agentSettingsForm.$setValidity('FTSDisabled', false);
            return;
        }

        const ftsSearchExtractionMethod = $scope.agentFormModel.assistantExtractionMethods.getFTSSearchExtractionMethod();
        if (!ftsSearchExtractionMethod.selected) {
            // clear the validation status if method is deselected.
            $scope.agentSettingsForm.$setValidity('FTSDisabled', true);
            return;
        }

        $scope.extractionMethodLoaderFlags[ExtractionMethod.FTS_SEARCH] = true;

        // pass a fake repository info object with only an id because we don't care for the location
        RepositoriesRestService.getRepositoryModel({id: $scope.agentFormModel.repositoryId}).then((repositoryModel) => {
            $scope.ftsEnabled = repositoryModel.getParamValue(REPOSITORY_PARAMS.enableFtsIndex);
        })
            .catch((error) => {
                logAndShowError(error, 'ttyg.agent.messages.error_repository_config_loading');
            })
            .finally(() => {
                $scope.extractionMethodLoaderFlags[ExtractionMethod.FTS_SEARCH] = false;
                $scope.agentSettingsForm.$setValidity('FTSDisabled', $scope.ftsEnabled);
            });
    };

    /**
     * Opens a modal to copy the external integration for the currently edited agent.
     *
     * @function
     */
    $scope.openExternalIntegrationConfig = () => {
        const agent = $scope.agentFormModel;
        ExternalIntegrationModalService.open(agent);
    };

    /**
     * Handles the change in the repository id field. This is needed because
     * the FTS method configuration depends on the selected repository to be
     * able to validate if the FTS is enabled for that selected repository.
     */
    $scope.onRepositoryChange = () => {
        refreshValidations(true, true);
    };

    /**
     * Checks the status of the autocomplete index.
     * @param {AdditionalExtractionMethodFormModel|undefined} method - The extraction method to check the autocomplete
     * index for. This function can be called in the visibilitychange handler, so the method might not be defined.
     * If not provided, it will check the 'Autocomplete IRI discovery search' method from the additional extraction
     * methods.
     */
    $scope.checkAutocompleteIndexEnabled = (method) => {
        if (!method) {
            method = $scope.agentFormModel.additionalExtractionMethods.additionalExtractionMethods
                .find((extractionMethod) => extractionMethod.method === AdditionalExtractionMethod.AUTOCOMPLETE_IRI_DISCOVERY_SEARCH);
        }
        // If the method is not selected, we don't need to check the autocomplete index status.
        // Also, if the repository id is not set, we can't check the autocomplete index status.
        if (method && !method.selected || !$scope.agentFormModel.repositoryId) {
            return;
        }
        const selectedRepositoryInfo = getSelectedRepositoryInfo();
        AutocompleteService.checkAutocompleteStatus(selectedRepositoryInfo.repositoryId, selectedRepositoryInfo.repositoryLocation)
            .then((autocompleteEnabled) => {
                $scope.autocompleteEnabled = autocompleteEnabled;
                $scope.agentSettingsForm.$setValidity('autocompleteDisabled', autocompleteEnabled);
            })
            .catch((error) => {
                $scope.agentSettingsForm.$setValidity('autocompleteDisabled', false);
                toastr.error(getError(error));
            });
    };

    /**
     * Restores the default context size.
     */
    $scope.onRestoreDefaultContextSize = () => {
        $scope.agentFormModel.contextSize = $scope.agentFormModel.contextSizeCopy;
    };

    /**
     * Restores the default system instructions.
     */
    $scope.onRestoreDefaultSystemInstructions = () => {
        $scope.agentFormModel.instructions.systemInstruction = $scope.agentFormModel.instructions.systemInstructionCopy;
    };

    /**
     * Restores the default user instructions.
     */
    $scope.onRestoreDefaultUserInstructions = () => {
        $scope.agentFormModel.instructions.userInstruction = $scope.agentFormModel.instructions.userInstructionCopy;
    };

    /**
     * Handles the change in the temperature field. This is needed because the high temperature warning should be shown
     * when the temperature is higher than 1.
     */
    $scope.onTemperatureChange = () => {
        $scope.showHighTemperatureWarning = $scope.agentFormModel.temperature.value > 1;
    };

    /**
     * Handles the change in the system instructions field.
     */
    $scope.onSystemInstructionChange = () => {
        if ($scope.agentFormModel.instructions.systemInstruction !== '' && !$scope.showSystemInstructionWarning) {
            $scope.showSystemInstructionWarning = true;
            ModalService.openModalAlert({
                title: $translate.instant('ttyg.agent.create_agent_modal.form.system_instruction.overriding_system_instruction_warning.title'),
                message: $translate.instant('ttyg.agent.create_agent_modal.form.system_instruction.overriding_system_instruction_warning.body'),
            }).result
                .then(function() {
                    // Do nothing, just warning the user
                });
        }
        if ($scope.agentFormModel.instructions.systemInstruction === '') {
            $scope.showSystemInstructionWarning = false;
        }
    };

    /**
     * Opens the agent instructions explain modal.
     */
    $scope.onExplainAgentSettings = () => {
        const agentPayload = $scope.agentFormModel.toPayload();
        TTYGService.explainAgentSettings(agentPayload)
            .then((agentInstructionsExplain) => {
                const options = {
                    templateUrl: 'js/angular/ttyg/templates/modal/agent-instructions-explain-modal.html',
                    controller: 'AgentInstructionsExplainModalController',
                    windowClass: 'agent-instructions-explain-modal',
                    backdrop: 'static',
                    resolve: {
                        dialogModel: function() {
                            return {
                                agentInstructionsExplain,
                            };
                        },
                    },
                    size: 'lg',
                };
                $uibModal.open(options).result
                    .then(() => {
                        // Do nothing
                    });
            }).catch((error) => {
            toastr.error(getError(error, 0, TTYG_ERROR_MSG_LENGTH));
        });
    };

    /**
     * Populates autocomplete suggestions for the given input text.
     * @param {Event} event - The input event containing the search text.
     */
    $scope.getSuggestions = (event) => {
        const inputText = event.target.value;
        AutocompleteRestService.getAutocompleteSuggestions(inputText)
            .then(mapUriAsNtripleAutocompleteResponse)
            .then((suggestions) => {
                $scope.autocompleteSuggestions = suggestions.map((item) => ({text: item.value}));
            }).catch((error) => {
            toastr.error(getError(error, 0, TTYG_ERROR_MSG_LENGTH));
        });
    };

    /**
     * Handles the change event for the vector fields select dropdown.
     * Updates the connector fields based on the selected vector field.
     * @param {ExtractionMethodFormModel} extractionMethod - The extraction method being updated.
     */
    $scope.onVectorFieldsChange = function(extractionMethod) {
        extractionMethod.connectorFields = extractionMethod.selectedConnectorField ? [extractionMethod.selectedConnectorField] : [];
    };

    /**
     * Determines whether there is connector data available for the similarity search extraction method.
     * @returns {boolean} True if connector data exists, false otherwise.
     */
    $scope.hasConnectorData = () => {
        return !!($scope.connectorMap && Object.keys($scope.connectorMap).length);
    };

    /**
     * Determines whether the vector fields select dropdown should be shown for the similarity search extraction method.
     * @param {ExtractionMethodFormModel} extractionMethod - The extraction method to check.
     * @returns {boolean} True if vector fields dropdown should be shown, false otherwise.
     */
    $scope.shouldShowVectorFields = (extractionMethod) => {
        if (!extractionMethod) {
            return false;
        }

        const provider = extractionMethod.connectorType;
        const indexName = extractionMethod.similarityIndex;

        if (!isVectorProvider(provider)) {
            return false;
        }

        if (Array.isArray($scope.vectorFields)) {
            return $scope.vectorFields.length > 0;
        }

        const fields = getVectorFieldsFromMap(provider, indexName);

        return Array.isArray(fields) && fields.length > 0;
    };

    /**
     * Handles the change event for the similarity index select menu.
     * Updates the connector type, similarity index, and refreshes available vector fields.
     * @param {ExtractionMethodFormModel} extractionMethod - The extraction method being updated.
     */
    $scope.onSimilarityIndexChange = (extractionMethod) => {
        const option = extractionMethod && extractionMethod.similarityIndexOption;
        // The user needs to select a vector field again.
        extractionMethod.selectedConnectorField = undefined;
        extractionMethod.connectorFields = [];

        if (!option) {
            extractionMethod.connectorType = null;
            extractionMethod.similarityIndex = '';
            $scope.vectorFields = [];
            return;
        }

        extractionMethod.connectorType = option.provider;
        extractionMethod.similarityIndex = option.value;

        $scope.vectorFields = getVectorFieldsFromMap(option.provider, option.value);
    };

    // =========================
    // Private functions
    // =========================

    /**
     * Gets vector fields from the connector map for a given provider and index name.
     * @param {string} provider - The provider type.
     * @param {string} indexName - The index name.
     * @returns {Array} The vector fields array, or an empty array if not found.
     */
    const getVectorFieldsFromMap = (provider, indexName) => {
        const map = $scope.connectorMap || {};
        return (map[provider] && map[provider][indexName])
            ? map[provider][indexName]
            : [];
    };

    /**
     * Updates the vector fields in the select dropdown based on the selected extraction method.
     * @param extractionMethod
     */
    const updateVectorFields = (extractionMethod) => {
        const provider = extractionMethod.connectorType;
        const indexName = extractionMethod.similarityIndex;

        if (!isVectorProvider(provider)) {
            $scope.vectorFields = [];
            return;
        }

        $scope.vectorFields = getVectorFieldsFromMap(provider, indexName);
    };

    /**
     * Resolves the hint message for the agent model property.
     * This is needed because the hint contains a html link that
     * should be properly rendered.
     * @return {*}
     */
    function getModelHelpMessage() {
        // The hint contains a html link which should be properly rendered.
        const message = decodeHTML($translate.instant('ttyg.agent.create_agent_modal.form.model.hint'));
        return $sce.trustAsHtml(message);
    }

    /**
     * Mapping of agent operations to their respective handlers.
     * @type {{[AGENT_OPERATION.EDIT]: (function(*): Promise<void>), [AGENT_OPERATION.CLONE]: (function(*): Promise<void>), [AGENT_OPERATION.CREATE]: (function(*): Promise<void>)}}
     */
    const agentOperationHandlers = {
        [AGENT_OPERATION.CREATE]: (payload) => createAgent(payload),
        [AGENT_OPERATION.EDIT]: (payload) => editAgent(payload),
        [AGENT_OPERATION.CLONE]: (payload) => cloneAgent(payload),
    };

    /**
     * Creates a new agent with the given payload and closes the modal if no errors occur.
     * @param {*} newAgentPayload - the payload for the new agent.
     * @return {Promise<void>}
     */
    const createAgent = (newAgentPayload) => {
        $scope.savingAgent = true;
        return TTYGService.createAgent(newAgentPayload)
            .then((agentModel) => {
                $uibModalInstance.close(agentModel);
                toastr.success($translate.instant('ttyg.agent.messages.agent_save_successfully', {agentName: agentModel.name}));
            })
            .catch((error) => {
                toastr.error(getError(error, 0, TTYG_ERROR_MSG_LENGTH));
            })
            .finally(() => {
                $scope.savingAgent = false;
            });
    };

    /**
     * Sends the edit agent payload to the server and closes the modal if no errors occur.
     * @param {*} agentPayload - the payload for the agent to be edited.
     * @return {Promise<void>}
     */
    const editAgent = (agentPayload) => {
        $scope.savingAgent = true;
        return TTYGService.editAgent(agentPayload)
            .then((updatedAgent) => {
                $uibModalInstance.close(updatedAgent);
                toastr.success($translate.instant('ttyg.agent.messages.agent_save_successfully', {agentName: updatedAgent.name}));
            })
            .catch((error) => {
                toastr.error(getError(error, 0, TTYG_ERROR_MSG_LENGTH));
            })
            .finally(() => {
                $scope.savingAgent = false;
            });
    };

    /**
     * Clones the agent with the given payload and closes the modal if no errors occur.
     * @param {*} agentPayload
     * @return {Promise<void>}
     */
    const cloneAgent = (agentPayload) => {
        $scope.savingAgent = true;
        return TTYGService.createAgent(agentPayload)
            .then((updatedAgent) => {
                $uibModalInstance.close(updatedAgent);
                toastr.success($translate.instant("ttyg.agent.messages.agent_save_successfully", {agentName: updatedAgent.name}));
            })
            .catch((error) => {
                toastr.error(getError(error, 0, TTYG_ERROR_MSG_LENGTH));
            })
            .finally(() => {
                $scope.savingAgent = false;
            });
    };

    const logAndShowError = (error, errorMessageKey) => {
        logger.error(error);
        toastr.error($translate.instant(errorMessageKey));
    };

    const setExtractionMethodValidityStatus = () => {
        $scope.agentSettingsForm.extractionMethods.$setValidity('required', $scope.agentFormModel.hasExtractionMethodSelected());
    };

    const handleFTSExtractionMethodPanelToggle = (extractionMethod) => {
        $scope.checkIfFTSEnabled();
    };

    /**
     * Returns the repository info model for the selected repository in the form.
     *
     * @return {{repositoryLocation: (*|undefined), repositoryId: (*|undefined)}}
     */
    const getSelectedRepositoryInfo = () => {
        const selectRepositoryInfo = $scope.activeRepositoryList.find((repository) => repository.value === $scope.agentFormModel.repositoryId);
        return {
            repositoryId: selectRepositoryInfo ? selectRepositoryInfo.data.repository.id : undefined,
            repositoryLocation: selectRepositoryInfo ? selectRepositoryInfo.data.repository.location : undefined,
        };
    };

    const isVectorProvider = (p) => p === SimilarityInstanceType.OPENSEARCH || p === SimilarityInstanceType.ELASTICSEARCH;

    /**
     * Creates grouped options for ng-options with optgroups.
     * @param connectorMap - The connector map with similarity indexes.
     */
    const buildSimilarityIndexSelectOptions = (connectorMap) => {
        /** @type {SimilarityIndexOption[]} */
        $scope.similarityOptionsGrouped = [];
        Object.entries(connectorMap).forEach(([provider, types]) => {
            const groupLabel = $translate.instant(`ttyg.agent.create_agent_modal.form.similarity_instance_type_label.${provider}`);
            Object.keys(types || {}).forEach((typeName) => {
                $scope.similarityOptionsGrouped.push({
                    provider,
                    label: typeName,
                    value: typeName,
                    group: groupLabel,
                });
            });
        });
    };

    const handleSimilaritySearchExtractionMethodPanelToggle = (extractionMethod, clearSelectedVectorField = false) => {
        if (!extractionMethod.selected) {
            // clear the validation status if method is deselected.
            $scope.agentSettingsForm.$setValidity('missingIndex', true);
        }
        if (extractionMethod.selected) {
            // Check if the connector instances select is pristine (unchanged). Treat the field as pristine until it exists.
            // Because it's loaded with ng-if, on first pass through here, it won't exist yet, so we treat it as pristine.
            const connectorField = $scope.agentSettingsForm && $scope.agentSettingsForm.connectorInstances;
            const isConnectorPristine = connectorField ? connectorField.$pristine : true;
            // Don't reload the indexes and override the user selection if the connector instances field is pristine
            if ($scope.connectorMap && !isConnectorPristine) {
                return;
            }
        }
        if (!$scope.agentFormModel.repositoryId) {
            return;
        }
        if (extractionMethod.expanded) {
            $scope.extractionMethodLoaderFlags[extractionMethod.method] = true;
            const selectedRepositoryInfo = getSelectedRepositoryInfo();
            SimilarityService.getSimilarityIndexesWithVectorFields(selectedRepositoryInfo.repositoryId)
                .then((connectorMap) => {
                    $scope.connectorMap = connectorMap;
                    const indexes = buildSelectMenuOptions(connectorMap);
                    // if no indexes are found, selection (connectorsMap) will be cleaned and the info message will be
                    // shown to the user again
                    buildSimilarityIndexSelectOptions(connectorMap);
                    updateSelectedSimilarityIndex(indexes, extractionMethod);
                    updateVectorFields(extractionMethod);

                    $scope.agentSettingsForm.$setValidity('missingIndex', !extractionMethod.selected || $scope.hasConnectorData());
                    // Initially, when there is no selection, the first index is selected, and we need to trigger the
                    // onSimilarityIndexChange to set the connector fields.
                    if (clearSelectedVectorField || (extractionMethod.connectorFields && extractionMethod.connectorFields.length === 0)) {
                        $scope.onSimilarityIndexChange(extractionMethod);
                    }
                })
                .catch((error) => {
                    $scope.connectorMap = null;
                    $scope.agentSettingsForm.$setValidity('missingIndex', false);
                    logAndShowError(error, 'ttyg.agent.messages.error_similarity_indexes_loading');
                })
                .finally(() => {
                    $scope.extractionMethodLoaderFlags[extractionMethod.method] = false;
                });
        }
    };

    /**
     * Updates the selected similarity index for the extraction method.
     *
     * @param {SelectMenuOptionsModel[]} indexes - The list of all available similarity indexes.
     * @param {Object} extractionMethod - The extraction method object containing the currently selected similarity index.
     */
    const updateSelectedSimilarityIndex = (indexes, extractionMethod) => {
        if (!indexes.length) {
            extractionMethod.connectorType = '';
            extractionMethod.similarityIndex = '';
            extractionMethod.similarityIndexOption = undefined;
            return;
        }

        const currentType = extractionMethod.connectorType;
        const currentName = extractionMethod.similarityIndex;

        // Find previously selected index or default to the first one
        const matchedIndex = indexes.find((index) =>
            index.data.connectorType === currentType &&
            index.value === currentName,
        );
        const selectedIndex = matchedIndex || indexes[0];

        extractionMethod.connectorType = selectedIndex.data.connectorType;
        extractionMethod.similarityIndex = selectedIndex.value;

        const options = $scope.similarityOptionsGrouped || [];
        const opt = options.find((option) =>
            option.provider === extractionMethod.connectorType &&
            option.value === extractionMethod.similarityIndex,
        );

        extractionMethod.similarityIndexOption = opt || undefined;
    };

    const buildSelectMenuOptions = (data) => {
        const result = [];

        Object.entries(data).forEach(([connectorType, indexes]) => {
            Object.entries(indexes).forEach(([indexName, fields]) => {
                result.push(
                    new SelectMenuOptionsModel({
                        label: indexName,
                        value: indexName,
                        data: {connectorType, connectorFields: fields},
                    }),
                );
            });
        });

        return result;
    };

    const handleRetrievalConnectorExtractionMethodPanelToggle = (extractionMethod) => {
        if (!$scope.agentFormModel.repositoryId) {
            return;
        }
        if (!extractionMethod.selected) {
            // clear the validation status if method is deselected.
            $scope.agentSettingsForm.$setValidity('missingConnector', true);
        }

        if (extractionMethod.expanded) {
            $scope.extractionMethodLoaderFlags[extractionMethod.method] = true;
            const selectedRepositoryInfo = getSelectedRepositoryInfo();
            ConnectorsService.getConnectorPrefixByName(CHAT_GPT_RETRIEVAL_CONNECTOR_NAME, selectedRepositoryInfo.repositoryId, selectedRepositoryInfo.repositoryLocation)
                .then((prefix) => ConnectorsService.getConnectorsByTypeAsSelectMenuOptions(prefix, selectedRepositoryInfo.repositoryId, selectedRepositoryInfo.repositoryLocation))
                .then((connectors) => {
                    $scope.retrievalConnectors = connectors;
                    $scope.agentSettingsForm.$setValidity('missingConnector', !extractionMethod.selected || !!(connectors && connectors.length));
                    updateSelectedRetrievalConnector($scope.retrievalConnectors, extractionMethod);
                })
                .catch((error) => {
                    $scope.agentSettingsForm.$setValidity('missingConnector', false);
                    logAndShowError(error, 'ttyg.agent.messages.error_retrieval_connectors_loading');
                })
                .finally(() => {
                    $scope.extractionMethodLoaderFlags[extractionMethod.method] = false;
                });
        }
    };

    /**
     * Updates the selected retrieval connector for the extraction method.
     *
     * @param {SelectMenuOptionsModel[]} retrievalConnectors - The list of all available connectors.
     * @param {Object} extractionMethod - The extraction method object containing the currently selected retrieval connector.
     */
    const updateSelectedRetrievalConnector = (retrievalConnectors, extractionMethod) => {
        if (retrievalConnectors.length === 0) {
            // If no connector are available, clear the similarity index.
            extractionMethod.retrievalConnectorInstance = null;
            return;
        }
        const selectedConnector = retrievalConnectors.find((connector) => connector.value === extractionMethod.retrievalConnectorInstance);

        // If the selected connector is not found in the current list, default to the first index.
        extractionMethod.retrievalConnectorInstance = selectedConnector
            ? selectedConnector.value
            : retrievalConnectors[0].value;
    };

    const extractionPanelToggleHandlers = {
        [ExtractionMethod.FTS_SEARCH]: (extractionMethod) => handleFTSExtractionMethodPanelToggle(extractionMethod),
        [ExtractionMethod.SPARQL]: (extractionMethod) => {
        },
        [ExtractionMethod.SIMILARITY]: (extractionMethod) => handleSimilaritySearchExtractionMethodPanelToggle(extractionMethod),
        [ExtractionMethod.RETRIEVAL]: (extractionMethod) => handleRetrievalConnectorExtractionMethodPanelToggle(extractionMethod),
    };

    const additionalExtractionPanelToggleHandlers = {
        [AdditionalExtractionMethod.AUTOCOMPLETE_IRI_DISCOVERY_SEARCH]: (extractionMethod) => handleAutocompleteExtractionMethodPanelToggle(extractionMethod),
        [AdditionalExtractionMethod.IRI_DISCOVERY_SEARCH]: () => {
        },
    };

    const handleAutocompleteExtractionMethodPanelToggle = (extractionMethod) => {
        if (!extractionMethod.selected) {
            // clear the validation status if method is deselected.
            $scope.agentSettingsForm.$setValidity('autocompleteDisabled', true);
        }
        $scope.checkAutocompleteIndexEnabled(extractionMethod);
    };

    const refreshValidations = (clearIndexSelection = false, clearRetrievalConnectorSelection = false) => {
        $scope.checkIfFTSEnabled();
        $scope.updateSimilaritySearchPanel(clearIndexSelection);
        $scope.updateRetrievalConnectorPanel(clearRetrievalConnectorSelection);
        $scope.checkAutocompleteIndexEnabled();
    };

    const onTabVisibilityChanged = () => {
        if (!document.hidden) {
            refreshValidations();
        }
    };

    const refreshSliderById = (elementId, value) => {
        const slider = document.getElementById(elementId);
        if (slider) {
            slider.value = value;
        }
    };

    // =========================
    // Subscriptions
    // =========================

    const removeAllSubscribers = () => {
        document.removeEventListener("visibilitychange", onTabVisibilityChanged);
    };

    document.addEventListener("visibilitychange", onTabVisibilityChanged);

    // Deregister the watcher when the scope/directive is destroyed
    $scope.$on('$destroy', removeAllSubscribers);


    // =========================
    // Initialization
    // =========================

    const init = () => {
        // Set repository id to null if it is missing from the list of active repositories
        const selectedRepositoryInfo = getSelectedRepositoryInfo();
        if (!selectedRepositoryInfo.repositoryId) {
            $scope.agentFormModel.repositoryId = null;
        }
        // Delay the validation status setting because angular form ngmodel is not present immediately
        setTimeout(setExtractionMethodValidityStatus, 0);
        $uibModalInstance.rendered.then(() => {
            refreshSliderById('temperatureSlider', $scope.agentFormModel.temperature.value);
            refreshSliderById('topPSlider', $scope.agentFormModel.topP.value);
        });
    };
    init();
}
